import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  Link as LinkIcon,
  Add as AddIcon,
  Receipt as ReceiptIcon,
} from '@mui/icons-material';
import config from '../config';

export interface PendingInvoice {
  id: string;
  contractor_name: string;
  invoice_date: string;
  total: string;
  document_type?: string;
}

interface CandidateTransaction {
  type: 'bank_transaction' | 'bank_statement_transaction';
  id: string;
  date: string | null;
  label: string;
  amount: string | null;
  source: string;
}

interface ConfirmNewExpenseDialogProps {
  open: boolean;
  invoice: PendingInvoice | null;
  /** Called for "Not now" (or closing the dialog) — the record stays pending, no API call. */
  onDismiss: () => void;
  /** Called after a successful link or confirm — the record is no longer pending. */
  onResolved: () => void;
  /** Skips the "is this a new expense, or proof of an existing transaction?" landing screen and
   * opens straight into one of the two flows — used by BulkInvoiceReviewDialog's per-row "Add to
   * transaction" button, where the choice has already been made by which button was clicked. */
  initialMode?: 'choose' | 'link' | 'confirm';
}

/**
 * Shown right after a standalone receipt/invoice upload (no bank transaction attached at upload
 * time) — the "what is this?" decision the backend now requires before it counts toward any
 * spend total (see Invoice.confirmed). Also reused from the Receipts & invoices grid's own
 * "Pending confirmation" section, for a document the user dismissed with "Not now" earlier and
 * is now coming back to.
 */
const ConfirmNewExpenseDialog: React.FC<ConfirmNewExpenseDialogProps> = ({ open, invoice, onDismiss, onResolved, initialMode = 'choose' }) => {
  const [mode, setMode] = useState<'choose' | 'link' | 'confirm'>('choose');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // "Add as new transaction" fields
  const [date, setDate] = useState('');
  const [amount, setAmount] = useState('');

  // "Link to an existing transaction" search
  const [query, setQuery] = useState('');
  const [candidates, setCandidates] = useState<CandidateTransaction[]>([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<CandidateTransaction | null>(null);

  const token = localStorage.getItem('auth_token');
  const headers = () => ({ 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' });

  useEffect(() => {
    if (open && invoice) {
      setMode(initialMode);
      setError(null);
      setDate(invoice.invoice_date || '');
      setAmount(invoice.total || '');
      setQuery('');
      setCandidates([]);
      setSelectedCandidate(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, invoice]);

  useEffect(() => {
    if (mode !== 'link') return;
    setCandidatesLoading(true);
    const handle = setTimeout(() => {
      fetch(`${config.apiUrl}/api/accounting/unlinked-transactions?q=${encodeURIComponent(query)}`, { headers: headers() })
        .then(res => res.ok ? res.json() : { results: [] })
        .then(d => setCandidates(d.results || []))
        .finally(() => setCandidatesLoading(false));
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, query]);

  if (!invoice) return null;

  const documentLabel = invoice.document_type === 'Receipt' ? 'receipt' : 'invoice';

  const handleLinkConfirm = async () => {
    if (!selectedCandidate) return;
    setSaving(true);
    setError(null);
    try {
      const body = selectedCandidate.type === 'bank_transaction'
        ? { bank_transaction_id: selectedCandidate.id }
        : { bank_statement_transaction_id: selectedCandidate.id };
      const res = await fetch(`${config.apiUrl}/api/invoices/${invoice.id}/link-proof`, {
        method: 'POST', headers: headers(), body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to link transaction');
      }
      onResolved();
    } catch (e: any) {
      setError(e.message || 'Failed to link transaction');
    } finally {
      setSaving(false);
    }
  };

  const handleAddAsNewConfirm = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(`${config.apiUrl}/api/invoices/${invoice.id}/confirm`, {
        method: 'POST', headers: headers(), body: JSON.stringify({ invoice_date: date, total: amount }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Failed to confirm expense');
      }
      onResolved();
    } catch (e: any) {
      setError(e.message || 'Failed to confirm expense');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onDismiss} maxWidth="sm" fullWidth>
      <DialogTitle>
        {mode === 'choose' && `${invoice.contractor_name} — is this a new expense, or proof of an existing transaction?`}
        {mode === 'link' && `${invoice.contractor_name} — link to an existing transaction`}
        {mode === 'confirm' && `${invoice.contractor_name} — is this data correct?`}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {mode === 'choose' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              This {documentLabel} isn't counted in your totals yet. Let us know what it is so it's tracked correctly.
            </Typography>
            <Button variant="outlined" startIcon={<LinkIcon />} onClick={() => setMode('link')} sx={{ justifyContent: 'flex-start', py: 1.25 }}>
              Link to an existing transaction
            </Button>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setMode('confirm')} sx={{ justifyContent: 'flex-start', py: 1.25 }}>
              Add as a new transaction
            </Button>
          </Box>
        )}

        {mode === 'link' && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Pick the bank or statement transaction this {documentLabel} is proof of.
            </Typography>
            <Autocomplete
              options={candidates}
              loading={candidatesLoading}
              value={selectedCandidate}
              onChange={(_, val) => setSelectedCandidate(val)}
              onInputChange={(_, val) => setQuery(val)}
              getOptionLabel={(o) => o.label}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderOption={(props, option) => {
                const { key, ...optionProps } = props as any;
                return (
                  <Box component="li" key={key} {...optionProps} sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <Box>
                      <Typography variant="body2">{option.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.date} · {option.source}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{option.amount}</Typography>
                  </Box>
                );
              }}
              renderInput={(params) => (
                <TextField {...params} label="Search transactions" placeholder="e.g. merchant name" autoFocus
                  InputProps={{ ...params.InputProps, endAdornment: (<>{candidatesLoading ? <CircularProgress size={16} /> : null}{params.InputProps.endAdornment}</>) }}
                />
              )}
            />
          </Box>
        )}

        {mode === 'confirm' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Here's the date and amount we read off the {documentLabel} — check they're correct before confirming.
            </Typography>
            <TextField label="Date" type="date" value={date} onChange={(e) => setDate(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField label="Amount" value={amount} onChange={(e) => setAmount(e.target.value)} fullWidth InputProps={{ startAdornment: <Box sx={{ mr: 0.5 }}>£</Box> }} />
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {mode !== 'choose' && (
          <Button onClick={() => setMode('choose')} disabled={saving} sx={{ mr: 'auto' }}>Back</Button>
        )}
        {mode === 'choose' && (
          <Button onClick={onDismiss} color="inherit" sx={{ mr: 'auto' }}>Not now</Button>
        )}
        {mode === 'link' && (
          <Button variant="contained" onClick={handleLinkConfirm} disabled={!selectedCandidate || saving} startIcon={saving ? <CircularProgress size={16} /> : <LinkIcon />}>
            Link
          </Button>
        )}
        {mode === 'confirm' && (
          <Button variant="contained" onClick={handleAddAsNewConfirm} disabled={saving || !date || !amount} startIcon={saving ? <CircularProgress size={16} /> : <ReceiptIcon />}>
            Confirm expense
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmNewExpenseDialog;
